import { useMemo } from "react";
import "./MindMappingReportAFifthSection.css";

/* ── Tier classification for Indian states ───────────────────── */
const TIER1_STATES = [
  "maharashtra", "karnataka", "delhi", "tamil nadu", "telangana",
  "haryana", "gujarat", "andhra pradesh"
];
const TIER2_STATES = [
  "rajasthan", "kerala", "west bengal", "madhya pradesh",
  "uttar pradesh", "punjab", "odisha"
];

function getStateTier(state) {
  if (!state) return { tier: "Tier 2", label: "Emerging Ecosystem", fundingAccess: "Moderate" };
  const s = state.toLowerCase().trim();
  if (TIER1_STATES.some(t => s.includes(t))) {
    return { tier: "Tier 1", label: "Prime Startup Hub", fundingAccess: "High" };
  }
  if (TIER2_STATES.some(t => s.includes(t))) {
    return { tier: "Tier 2", label: "Emerging Ecosystem", fundingAccess: "Moderate" };
  }
  return { tier: "Tier 3", label: "Opportunity-Rich Market", fundingAccess: "Lower — but govt support strong" };
}

/* ── Sector × location advantage lookup ─────────────────────── */
const SECTOR_TYPE_ADVANTAGE = {
  tertiary:  "Services & digital startups face the lowest geographic friction — remote-first models and cloud infrastructure let you serve pan-India from Day 1.",
  secondary: "Manufacturing and production startups benefit from proximity to industrial clusters, logistics corridors, and PLI scheme zones. Location matters for supply chain efficiency.",
  primary:   "Agriculture, mining, and primary sector startups thrive by solving hyperlocal problems — your district-level insight is a competitive moat that urban founders cannot replicate.",
};

/* ── Government schemes by sector type ──────────────────────── */
const GOVT_SCHEMES = {
  tertiary:  ["Startup India (DPIIT Recognition)", "Digital India Initiative", "SIDBI ARISE Fund", "Software Technology Parks of India (STPI)"],
  secondary: ["Make in India", "Production Linked Incentive (PLI)", "MSME Credit Guarantee Scheme", "National Manufacturing Policy"],
  primary:   ["PM-KISAN Agri Fund", "NABARD Rural Innovation", "Agri-Tech Startup Incubation (RKVY)", "National Beekeeping & Honey Mission"],
};

/* ── Talent pool assessment ──────────────────────────────────── */
function getTalentSignal(state, course) {
  const tier = getStateTier(state).tier;
  const isEngg = course && /b\.?tech|engineering|bsc|msc|mba/i.test(course);
  if (tier === "Tier 1" && isEngg) return "Strong — dense engineering college network and active alumni startup community nearby.";
  if (tier === "Tier 1") return "Good — established professional network and co-working ecosystem available.";
  if (tier === "Tier 2") return "Moderate — emerging talent pool; consider remote hiring and college partnerships.";
  return "Niche — leverage local domain expertise and consider IIT/IIM remote mentorship programs.";
}

const MindMappingReportAFifthSection = ({ res, toggle }) => {
  const stateInfo = useMemo(() => getStateTier(res?.state), [res?.state]);
  const sectorType = (res?.sector_type ?? "tertiary").toLowerCase();
  const schemes = GOVT_SCHEMES[sectorType] ?? GOVT_SCHEMES.tertiary;
  const sectorAdvantage = SECTOR_TYPE_ADVANTAGE[sectorType] ?? SECTOR_TYPE_ADVANTAGE.tertiary;
  const talentSignal = getTalentSignal(res?.state, res?.course);

  const summaryRows = [
    {
      sno: "5 A",
      factor: "State / Location Tier",
      view: `${res?.state ?? "India"} — ${stateInfo.tier} (${stateInfo.label})`,
      highlight: `Funding Access: ${stateInfo.fundingAccess}`,
    },
    {
      sno: "",
      factor: "District Market",
      view: res?.district ? `${res.district}, ${res?.state ?? ""}` : "Not specified",
      highlight: stateInfo.tier === "Tier 3"
        ? "Tier 3 districts often hold underserved problems — first-mover advantage is stronger here."
        : stateInfo.tier === "Tier 1"
        ? "High investor visibility; early-stage demo days and startup events are accessible."
        : "Growing startup ecosystem; government incubation centers expanding rapidly in this region.",
    },
    {
      sno: "",
      factor: "Sector × Location Fit",
      view: `${res?.sector_type ?? "Tertiary"} sector in ${res?.state ?? "India"}`,
      highlight: sectorAdvantage,
    },
    {
      sno: "",
      factor: "Relevant Govt Schemes",
      view: schemes.slice(0, 2).join(", "),
      highlight: `Also explore: ${schemes.slice(2).join(", ")}`,
    },
    {
      sno: "",
      factor: "Talent Pool Signal",
      view: `${res?.course ?? "General"} background in ${res?.state ?? "India"}`,
      highlight: talentSignal,
    },
  ];

  return (
    <div className="mind-mapping-report-a-fifth-container">
      <div className={toggle ? "table-adjust" : "mind-mapping-report-a-fifth-final-table"}>
        <span className="mind-mapping-report-a-fifth-corner mind-mapping-report-a-fifth-corner-top-left" />
        <span className="mind-mapping-report-a-fifth-corner mind-mapping-report-a-fifth-corner-top-right" />
        <span className="mind-mapping-report-a-fifth-corner mind-mapping-report-a-fifth-corner-bottom-left" />
        <span className="mind-mapping-report-a-fifth-corner mind-mapping-report-a-fifth-corner-bottom-right" />

        <div className="mind-mapping-report-a-fifth-module-title">
          <span className="module-prefix">Module 5:</span> Location Intelligence & Market Access
        </div>

        <div className="mind-mapping-report-a-fifth-table-scroll-wrapper">
          <table className="mind-mapping-report-a-fifth-inner-table">
            <thead>
              <tr>
                <th className="mind-mapping-report-a-fifth-col-sno">S.NO</th>
                <th>Factor</th>
                <th>Your Profile</th>
                <th>Investor / Market Signal</th>
              </tr>
            </thead>
            <tbody>
              {summaryRows.map((row, index) => (
                <tr key={index}>
                  {index === 0 && (
                    <td
                      className="mind-mapping-report-a-fifth-col-sno"
                      rowSpan={summaryRows.length}
                    >
                      5 A
                    </td>
                  )}
                  <td>{row.factor}</td>
                  <td className="mind-mapping-report-a-fifth-user-val">{row.view}</td>
                  <td className="mind-mapping-report-a-fifth-highlight">{row.highlight}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MindMappingReportAFifthSection;
